import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSort, MatPaginator } from '@angular/material';
import { ObservableMedia, MediaChange } from '@angular/flex-layout';

import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { first } from 'rxjs/operators/first';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { takeUntil } from 'rxjs/operators/takeUntil';
import { combineLatest } from 'rxjs/observable/combineLatest';

import { format, isBefore, isSameDay, isValid } from 'date-fns';

import { DataTableSource } from '../../classes/DataTableSource/datatablesource';

import { ApplicationService, CollegeInfo, CollegeTermInfo } from '../../services/application/application.service';
import { DialogService } from '../../services/dialogs/dialogs.service';
import { ErrorService } from '../../services/error/error.service';
import { SplashService } from '../../services/splash/splash.service';
import { UserInfo, UserService } from '../../services/user/user.service';

import { CollegeSchoolYearPipe } from '../../pipes/collegeschoolyear.pipe';
import { CollegeTermPipe } from '../../pipes/collegeterm.pipe';

@Component({
    selector: 'app-manage-terms',
    styleUrls: ['manageterms.component.css'],
    templateUrl: 'manageterms.component.html',
})
export class ManageTermsComponent implements OnInit, OnDestroy {
    @ViewChild('paginator') paginator: MatPaginator;
    @ViewChild(MatSort) sorter: MatSort;
    private _subrelease: Subject<void> = new Subject<void>();

    AdminCollege: CollegeInfo;
    AdminCollegeID: number;
    AllCollegeYears: string[];
    AllHeaders: string[] = ['CollegeYear', 'TermCode', 'Term', 'DateBegin', 'DateEnd', 'Actions'];
    Colleges: CollegeInfo[];
    CollegeTermChoices: CollegeTermInfo[] = [{ TermCode: '1' }, { TermCode: '2' }, { TermCode: '3' }];
    NoData: boolean;
    PartialViewReady: boolean;
    ShowingTermEntryPanel: boolean;
    SmallDisplay: boolean;
    TermsData: DataTableSource<CollegeTermInfo>;
    TermEntryForm: FormGroup;
    TermsList: CollegeTermInfo[];
    User: UserInfo;
    VisibleHeaders: string[];
    VisibleColumns: string[];

    constructor(
        private appService: ApplicationService,
        private collegeSchoolYearPipe: CollegeSchoolYearPipe,
        private collegeTermPipe: CollegeTermPipe,
        private dialogService: DialogService,
        private errorService: ErrorService,
        private fb: FormBuilder,
        private mediaChange: ObservableMedia,
        private splashService: SplashService,
        private userService: UserService
    ) {}

    ngOnInit() {
        const self = this;

        self.AdminCollege = null;
        self.Colleges = null;
        self.NoData = false;
        self.PartialViewReady = false;
        self.ShowingTermEntryPanel = false;
        self.TermsList = null;
        self.TermsData = new DataTableSource<CollegeTermInfo>([], self.paginator, self.sorter, self.SortTerms.bind(self));
        self.User = null;

        self.SmallDisplay = self.mediaChange.isActive('xs') || self.mediaChange.isActive('sm');

        self.mediaChange
            .asObservable()
            .pipe(takeUntil(self._subrelease))
            .subscribe(media => {
                self.SmallDisplay = media.mqAlias === 'xs' || media.mqAlias === 'sm';
                self.VisibleHeaders = self.SmallDisplay ? ['SmallDisplay'] : self.AllHeaders.slice();
                self.VisibleColumns = self.NoData ? ['NoData'] : self.VisibleHeaders.slice();
            });

        // Setup the form group
        self.TermEntryForm = self.fb.group({
            CollegeSchoolYear: ['', Validators.required], //  ng-disabled="vm.NewTermParams.TermWindowID != null"
            TermCode: ['', Validators.required], //  ng-disabled="vm.NewTermParams.TermWindowID != null"
            DateBegin: [null, Validators.required],
            DateEnd: [null, Validators.required],
            TermWindowID: [null],
        });

        // Get the first non-null UserInfo object so we can begin setting up the component
        self.userService.UserSubject.pipe(
            takeUntil(self._subrelease),
            first(user => user && !!user.AuthToken)
        ).subscribe(user => {
            self.User = user;

            // Set up constant monitoring of the user object, to catch AuthToken updates.
            self.userService.UserSubject.pipe(takeUntil(self._subrelease)).subscribe(_user => {
                self.User = _user;
            });

            // Load static data for use in the compoment.
            const collegeObs = self.appService.GetAllColleges(self.User.AuthToken);
            const allCollegeYearsObs = self.appService.GetAllCollegeYears();
            const adminCollegeObs = self.appService.SelectedAdminCollegeIDSubject;

            combineLatest(collegeObs, allCollegeYearsObs, adminCollegeObs, function(colleges, collegeyears, adminCollegeID) {
                return {
                    AdminCollegeID: adminCollegeID,
                    Colleges: colleges,
                    CollegeYears: collegeyears,
                };
            })
                .pipe(takeUntil(self._subrelease))
                .subscribe(
                    packet => {
                        self.Colleges = packet.Colleges;
                        self.AllCollegeYears = packet.CollegeYears;
                        self.AdminCollegeID = packet.AdminCollegeID;
                        self.AdminCollege = null;

                        if (self.AdminCollegeID != null && self.Colleges) {
                            self.AdminCollege = self.Colleges.find(item => {
                                return item.CollegeID === self.AdminCollegeID;
                            });
                        }
                        // If no college is selected, try to pre-select a college, deferring to Polk State if available.
                        // if (self.AdminCollegeID != null && self.Colleges) {
                        //     self.AdminCollege = self.Colleges.find(item => {return item.CollegeID == 2;});

                        //     if (!self.AdminCollege) self.AdminCollege = self.Colleges[0];
                        // }

                        // Load the terms for this college, if one has been selected.
                        if (self.AdminCollege) {
                            self.appService.GetAvailableTerms(self.User.AuthToken, self.AdminCollege.CollegeID).subscribe(
                                terms => {
                                    self.TermsList = terms;

                                    if (!self.TermsList || !self.TermsList.length) {
                                        self.NoData = true;

                                        // Put in a fake record to trip the display message
                                        self.TermsData.dataChange.next([{}]);
                                    } else {
                                        self.NoData = false;
                                        self.TermsData.dataChange.next(self.TermsList);
                                    }

                                    self.VisibleHeaders = self.SmallDisplay ? ['SmallDisplay'] : self.AllHeaders.slice();
                                    self.VisibleColumns = self.NoData ? ['NoData'] : self.VisibleHeaders.slice();

                                    // Ready for component use.
                                    self.PartialViewReady = true;
                                },
                                error => {
                                    self.errorService.HandleServiceError(error);
                                }
                            );
                        } else {
                            // No college selected. Release component for viewing.
                            self.PartialViewReady = true;
                        }
                    },
                    error => {
                        self.errorService.HandleServiceError(error);
                    }
                );
        });
    }

    ngOnDestroy() {
        this._subrelease.next();
        this._subrelease.complete();
    }

    // #region Add Term Cancel Handler Method

    AddTermCancelHandler() {
        const self = this;

        // Null out the form, then close the panel.
        self.TermEntryForm.reset();

        self.ShowingTermEntryPanel = false;
    }

    // #endregion

    // #region Add Term Handler Method

    AddTermHandler() {
        const self = this;

        const form = self.TermEntryForm.getRawValue();
        if (!form) {
            return;
        }

        // Validate that the dates are in proper ordering (DateBegin <= DateEnd)
        if (!form.DateBegin || !isValid(form.DateBegin)) {
            self.splashService.SnackbarError('Term beginning date is missing or invalid.');
            return;
        }

        if (!form.DateEnd || !isValid(form.DateEnd)) {
            self.splashService.SnackbarError('Term ending date is missing or invalid.');
            return;
        }

        const dateend = <Date>form.DateEnd;
        const datebegin = <Date>form.DateBegin;

        if (isBefore(dateend, datebegin) || isSameDay(dateend, datebegin)) {
            self.splashService.SnackbarError('Term ending date is set as before or on the same day as the beginning date.');
            return;
        }

        // Get a confirmation
        self.dialogService
            .OpenConfirmationDialog({
                TitleText: 'Confirmation Requested',
                MessageText: `You are about to add the following new college term window for ${
                    self.AdminCollege.CollegeName
                }:<br /><br />
            <strong>College School Year: </strong>${self.collegeSchoolYearPipe.transform(
                form.CollegeSchoolYear,
                self.AdminCollege.CollegeID
            )}<br />
            <strong>Term: </strong>${self.collegeTermPipe.transform(form.TermCode)}<br />
            <strong>Term Code: </strong>${form.TermCode}<br />
            <strong>Beginning Date: </strong>${format(form.DateBegin, 'M/DD/YYYY')}<br />
            <strong>Ending Date: </strong>${format(form.DateEnd, 'M/DD/YYYY')}<br /><br />Are you sure you wish to continue?`,
                OKText: 'Yes - Add Term',
                CancelText: 'No - CANCEL',
            })
            .subscribe(result => {
                if (result === true) {
                    // User has opted to proceed. Signal the system to add the term window to the college.
                    self.appService
                        .AddTermWindowToCollege(
                            self.User.AuthToken,
                            self.AdminCollege.CollegeID,
                            form.CollegeSchoolYear,
                            form.TermCode,
                            form.DateBegin,
                            form.DateEnd
                        )
                        .subscribe(
                            terms => {
                                // Toast the success
                                self.splashService.SnackbarSuccess('New college term window added successfully.');

                                // Update the list of known college terms
                                self.TermsList = terms;
                                self.TermsData.Update(terms);

                                // Reset the term form
                                self.TermEntryForm.reset();

                                // Hide the panel
                                self.ShowingTermEntryPanel = false;
                            },
                            error => {
                                self.errorService.HandleServiceError(error);
                            }
                        );
                }
            });
    }

    // #endregion

    // #region Remove Term Method

    RemoveTerm(term: CollegeTermInfo) {
        const self = this;

        if (!term) {
            return;
        }

        // Get a confirmation
        self.dialogService
            .OpenConfirmationDialog({
                TitleText: 'Confirmation Requested',
                MessageText:
                    `You have chosen to remove the following college term window for ${self.AdminCollege.CollegeName}:<br /><br />
            <strong>College School Year: </strong>${self.collegeSchoolYearPipe.transform(
                term.CollegeSchoolYear,
                self.AdminCollege.CollegeID
            )}<br />
            <strong>Term: </strong>${self.collegeTermPipe.transform(term.TermCode)}<br />
            <strong>Term Code: </strong>${term.TermCode}<br />
            <strong>Beginning Date: </strong>${format(term.DateBegin, 'M/DD/YYYY')}<br />
            <strong>Ending Date: </strong>${format(
                term.DateEnd,
                'M/DD/YYYY'
            )}<br /><br />Once removed, this term window must be added again in order for students to have access ` +
                    `to making new requests for this college term. Are you sure you wish to continue?`,
                OKText: 'Yes - Remove Term',
                CancelText: 'No - CANCEL',
            })
            .subscribe(result => {
                if (result === true) {
                    // User has opted to proceed. Signal the system to remove this term window from the college.
                    self.appService.RemoveTermWindowFromCollege(self.User.AuthToken, term.TermWindowID).subscribe(
                        terms => {
                            // Toast the success
                            self.splashService.SnackbarSuccess('College term window was removed successfully.');

                            // Update the list of known college terms
                            self.TermsList = terms;
                            self.TermsData.Update(terms);
                        },
                        error => {
                            self.errorService.HandleServiceError(error);
                        }
                    );
                }
            });
    }

    // #endregion

    // #region Show Term Entry Editor Panel Method

    ShowTermEntryEditorPanel(term?: CollegeTermInfo) {
        const self = this;

        // Set up the formgroup to prepare for editing. If we were provided a college term, use those values to prefill the formgroup.
        if (term) {
            const newform = {
                CollegeSchoolYear: term.CollegeSchoolYear, //  ng-disabled="vm.NewTermParams.TermWindowID != null"
                TermCode: term.TermCode, //  ng-disabled="vm.NewTermParams.TermWindowID != null"
                DateBegin: term.DateBegin, // [null, Validators.required],
                DateEnd: term.DateEnd, // [null, Validators.required],
                TermWindowID: term.TermWindowID, // [null]
            };

            // Patch the values
            self.TermEntryForm.patchValue(newform);

            // Set disabled elements
            self.TermEntryForm.get('CollegeSchoolYear').disable();
            self.TermEntryForm.get('TermCode').disable();
        } else {
            // Clear the form
            const newform = {
                CollegeSchoolYear: '',
                TermCode: '',
                DateBegin: null,
                DateEnd: null,
                TermWindowID: null,
            };

            self.TermEntryForm.patchValue(newform);

            // Enable all elements
            self.TermEntryForm.get('CollegeSchoolYear').enable();
            self.TermEntryForm.get('TermCode').enable();
        }

        self.TermEntryForm.updateValueAndValidity({ emitEvent: false });

        self.ShowingTermEntryPanel = true;
    }

    // #endregion

    // #region Sort Terms Method

    SortTerms(First: CollegeTermInfo, Second: CollegeTermInfo) {
        const self = this;

        let PropA: string | number | Date;
        let PropB: string | number | Date;

        switch (self.sorter.active) {
            case 'CollegeYear':
                [PropA, PropB] = [First.CollegeSchoolYear, Second.CollegeSchoolYear];
                break;
            case 'TermCode':
                [PropA, PropB] = [First.TermCode, Second.TermCode];
                break;
            case 'Term':
                [PropA, PropB] = [self.collegeTermPipe.transform(First.TermCode), self.collegeTermPipe.transform(Second.TermCode)];
                break;
            case 'DateBegin':
                [PropA, PropB] = [First.DateBegin, Second.DateBegin];
                break;
            case 'DateEnd':
                [PropA, PropB] = [First.DateEnd, Second.DateEnd];
                break;
        }

        if (PropA instanceof Date) {
            return (isBefore(PropA, PropB) ? -1 : 1) * (self.sorter.direction === 'asc' ? 1 : -1);
        } else {
            const ValueA = PropA != null && PropA !== '' ? (isNaN(+PropA) ? PropA : +PropA) : PropA;
            const ValueB = PropB != null && PropB !== '' ? (isNaN(+PropB) ? PropB : +PropB) : PropB;

            return (ValueA < ValueB ? -1 : 1) * (self.sorter.direction === 'asc' ? 1 : -1);
        }
    }

    // #endregion

    // #region Update Term Handler Method

    UpdateTermHandler() {
        const self = this;

        const form = self.TermEntryForm.getRawValue();
        if (!form) {
            return;
        }

        // Validate that the dates are in proper ordering (DateBegin <= DateEnd)
        if (!form.DateBegin || !isValid(form.DateBegin)) {
            self.splashService.SnackbarError('Term beginning date is missing or invalid.');
            return;
        }

        if (!form.DateEnd || !isValid(form.DateEnd)) {
            self.splashService.SnackbarError('Term ending date is missing or invalid.');
            return;
        }

        const dateend = <Date>form.DateEnd;
        const datebegin = <Date>form.DateBegin;

        if (isBefore(dateend, datebegin) || isSameDay(dateend, datebegin)) {
            self.splashService.SnackbarError('Term ending date is set as before or on the same day as the beginning date.');
            return;
        }

        // Get a confirmation
        self.dialogService
            .OpenConfirmationDialog({
                TitleText: 'Confirmation Requested',
                MessageText: `You are about to update the following college term window for ${
                    self.AdminCollege.CollegeName
                }:<br /><br />
            <strong>College School Year: </strong>${self.collegeSchoolYearPipe.transform(
                form.CollegeSchoolYear,
                self.AdminCollege.CollegeID
            )}<br />
            <strong>Term: </strong>${self.collegeTermPipe.transform(form.TermCode)}<br />
            <strong>Term Code: </strong>${form.TermCode}<br />
            <strong>Beginning Date: </strong>${format(form.DateBegin, 'M/DD/YYYY')}<br />
            <strong>Ending Date: </strong>${format(form.DateEnd, 'M/DD/YYYY')}<br /><br />Are you sure you wish to continue?`,
                OKText: 'Yes - Update Term',
                CancelText: 'No - CANCEL',
            })
            .subscribe(result => {
                if (result === true) {
                    // User has opted to proceed. Signal the system to add the term window to the college.
                    self.appService
                        .UpdateTermWindowForCollege(
                            self.User.AuthToken,
                            form.TermWindowID,
                            form.CollegeSchoolYear,
                            form.TermCode,
                            form.DateBegin,
                            form.DateEnd
                        )
                        .subscribe(
                            terms => {
                                // Toast the success
                                self.splashService.SnackbarSuccess('College term window updated successfully.');

                                // Update the list of known college terms
                                self.TermsList = terms;
                                self.TermsData.Update(terms);

                                // Reset the term form
                                self.TermEntryForm.reset();

                                // Hide the panel
                                self.ShowingTermEntryPanel = false;
                            },
                            error => {
                                self.errorService.HandleServiceError(error);
                            }
                        );
                }
            });
    }

    // #endregion
}



// WEBPACK FOOTER //
// ./src/app/manageterms/manageterms.component.ts